/* Aperçus boîte mail — EM-04 gift · EM-05 after-the-visit · EM-06 return
   reminder (TOV §26.1–26.3 · Bible §33/§28.1).
   The bodies are NOT written here: they come from '@/emails', read-only, and
   render through the same shell the inbox receives — preview is delivered
   parity. Rule 1 lives in that source: the review ask carries no mention of
   the gift, and the reminder describes an existing benefit.

   Migration pack messages (v3.3 WF-114): the page is the « Prévisualiser » door of the hub's « E-mails aux clients »
   tab (a page, not a dialog). Under the frame, what the message contains, read from the template itself — the gift, the
   code, the date and the button are the template's own sample, never invented here; the wallet pair is named as
   lib/wallet names it (« Apple Wallet » / « Google Wallet »); « Fermer » goes back to the tab. */
import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, EmptyState, KV, Note } from '@/ui'
import { TEMPLATES } from '@/emails/templates'
import { renderEmail } from '@/emails/render'
import { WALLETS } from '@/lib/wallet'
import { Retour } from './parts'

/* The frame’s micro caption under each preview is the first clause of the
   template’s own sendRule ('Envoyé +2 h après la visite · aucune mention du
   cadeau…'), so it is rendered from '@/emails' rather than restated here. */

const RETOUR = '/messages?onglet=emails'

export default function Apercu({ id }: { id: string }) {
  const t = TEMPLATES.find(x => x.id === id)
  const html = useMemo(() => (t ? renderEmail(t) : ''), [t])
  const frame = useRef<HTMLIFrameElement>(null)
  const [hauteur, setHauteur] = useState(560)
  const nav = useNavigate()

  /* the preview shows the whole mail — an inbox does not crop it either */
  const ajuster = () => {
    const doc = frame.current?.contentDocument
    if (doc) setHauteur(Math.max(360, doc.documentElement.scrollHeight + 2))
  }

  if (!t) return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 620 }}>
      <Retour to={RETOUR} label="E-mails aux clients" />
      <EmptyState title="Ce modèle n’existe pas."
        why="L’inventaire des messages ne contient aucun modèle sous cet identifiant." />
    </div>
  )

  const contenu: Array<[string, string]> = [
    ['Objet', t.subject],
    ...(t.gift ? [
      ['Cadeau', t.gift.prize] as [string, string],
      ['Valable jusqu’au', t.gift.expiry] as [string, string],
      ['Code cadeau', t.gift.code] as [string, string],
      ['Où le présenter', t.gift.location] as [string, string],
    ] : []),
    ...(t.cta ? [['Bouton', t.cta.label] as [string, string]] : []),
  ]

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }} data-testid="apercu-mail">
      <Retour to={RETOUR} label="E-mails aux clients" />
      <div>
        <p className="t-eyebrow" style={{ margin: '0 0 4px' }}>Boîte mail · aperçu</p>
        <h2 className="t-page" style={{ margin: 0 }}>{t.name}</h2>
      </div>

      <iframe ref={frame} title={`Aperçu — ${t.name}`} srcDoc={html} onLoad={ajuster}
        style={{ width: '100%', maxWidth: 620, height: hauteur, border: '1px solid var(--bmr-border)',
          borderRadius: 12, background: 'var(--bmr-surface-sunken)' }} />

      <div data-testid="apercu-contenu">
        <p className="t-eyebrow" style={{ margin: '0 0 8px' }}>Ce que contient ce message · exemple du modèle</p>
        <KV rows={contenu} />
      </div>

      {t.gift && (
        <Note tone="candour" data-testid="apercu-cadeau-note">
          Récupération d’un cadeau déjà gagné, indépendante du consentement marketing&nbsp;: aucun nouveau gain n’est créé, un
          renvoi présente le même cadeau. Depuis « {t.cta?.label ?? 'Voir mon cadeau'} », la cliente retrouve son code et peut
          l’ajouter à {WALLETS.apple.name} ou {WALLETS.google.name}.
        </Note>
      )}

      <p className="f-help" style={{ margin: 0 }}>De&nbsp;: {t.from}</p>
      <p className="f-help" style={{ margin: 0 }}>{t.sendRule}</p>
      <p className="f-help" style={{ margin: 0 }}>Aperçu de démonstration&nbsp;: le cadeau, le code, la date et le destinataire sont ceux de l’exemple du modèle. Aucun envoi depuis cette page.</p>
      <div className="actions">
        <Button variant="quiet" size="sm" onClick={() => nav(RETOUR)}>Fermer</Button>
      </div>
    </div>
  )
}
